import React, { useEffect, useRef, useState } from 'react';
import {
  AUTISM_ACCEPT_QUESTION_LABEL,
  AUTISM_CONCLUSION_HELPER_PROMPTS,
  AUTISM_CRITERIA_SECTIONS,
  AUTISM_CRITERION_C,
  AUTISM_CRITERION_D,
  AUTISM_DSM5_CRITERIA,
  AUTISM_EVIDENCE_INTRO,
  AUTISM_EVIDENCE_KEYS,
  AUTISM_EXTERNAL_EVIDENCE_NOTE,
  AUTISM_EXTERNAL_EVIDENCE_NOTE_TITLE,
  AUTISM_FOLLOW_THROUGH_OPTIONS,
  AUTISM_FOLLOW_THROUGH_QUESTION,
  AUTISM_IMPACT_SCALE,
  AUTISM_LONG_TERM_OPTIONS,
  AUTISM_LONG_TERM_QUESTION,
  AUTISM_OTHER_FACTOR_GROUPS,
  AUTISM_PRIORITY_DETAIL_LABEL,
  AUTISM_PRIORITY_PLANS_OPTIONS,
  AUTISM_PRIORITY_PLANS_QUESTION_LABEL,
  AUTISM_PRIORITY_PROBLEMS_QUESTION_LABEL,
  AUTISM_PRIORITY_SECTION_HINT,
  AUTISM_REPORTED_BY_OPTIONS,
  AUTISM_REPORTED_BY_QUESTION,
  AUTISM_RISKS_GATING_NOTE,
  AUTISM_RISKS_INTERVENTIONS_HINT,
  AUTISM_RISKS_INTERVENTIONS_LABEL,
  AUTISM_RISKS_QUESTION_LABEL,
  AUTISM_RISKS_SECTION_HINT,
  AUTISM_RISKS_SECTION_TITLE,
  AUTISM_THRESHOLD_QUESTION,
  AUTISM_YES_NO_OPTIONS,
} from '../../constants/autismTriageOutcomes';
import { CONCLUSION_PROMPT } from '../../constants/adhdTriageOutcomes';
import {
  AUTISM_SIGNPOSTING_GROUPS,
  AUTISM_SIGNPOSTING_HINT,
  AUTISM_SIGNPOSTING_OPTIONS,
  AUTISM_SIGNPOSTING_PROMPT,
  SIGNPOSTING_OTHER_LABEL,
} from '../../constants/autismSignposting';
import { collectRecordedUnmetNeeds } from '../../utils/autismUnmetNeeds';
import ReferralEvidencePanel from './ReferralEvidencePanel';
import {
  decisionTagPayload,
  factorLabelFor,
  otherFactorTagsByGroup,
  TAG_KIND,
  tagChipLabel,
  tagsOfKind,
} from '../../constants/evidenceTagging';
import {
  EvidenceQuestion,
  PatientProfileStrip,
  RiskStratificationSummary,
  StepProgress,
  TaggedLines,
  ToggleChoice,
  clinicianName,
} from './triageDecisionShared';
import { RmsProviderErrorBoundary } from './RmsProviderErrorBoundary';
import { surfacedItemSources } from '../../utils/otherFactorsSurfacing';
import TriageConfirmDialog from './TriageConfirmDialog';
import {
  DECISION_CHECK_GO_BACK,
  DECISION_CHECK_RECORD,
  DECISION_CHECK_TITLE,
  decisionCheckLines,
  decisionCheckSubtitle,
} from '../../utils/triageDecisionCheck';

/**
 * AutismTriageDecisionForm
 * ------------------------
 * The autism triage decision screens, built on the DSM-5 criterion structure
 * per the clinical lead's July 2026 review (see autismTriageOutcomes.js for
 * the full model and rationale). Three screens (TDI-394, Tom King's rulings
 * of 27 October 2026):
 *
 *   1. DSM-5 criteria — A1-A3 and B1-B4 each rated on the impact scale plus
 *      who-reports-it, long-standing and the threshold; criterion C with
 *      conditional onset follow-ups; criterion D. All nine required. Under
 *      each of A1-B4, the lines the clinician tagged to it.
 *   2. Barriers and priority problems — the lines the clinician tagged as a
 *      risk or an other factor, with their notes (and follow-through beside
 *      tagged support factors); the risks and priority-problems questions;
 *      one comment on other factors.
 *   3. Triage decision — a recap of what was recorded, the lines added to the
 *      conclusion, the conclusion and accept decision (both required) and
 *      signposting. No clinicians-involved control: autism triage is a
 *      dual-review flow, not a single joint-team decision.
 *
 * The screens show only what the clinician recorded or tagged in the history
 * review (R2, R-016): no form answers are matched to factors here, untagged
 * factors are not listed or prompted for, and nothing is pre-ticked from a
 * tag, including "reported by" (R4). The one recorded exception is a second
 * review, where the first review's answers start as the second reviewer's
 * (R-008). "View referral evidence" still opens every form answer, for the
 * clinician to read and tag by choice.
 *
 * Tags are owned by the parent, not held here: they are made in the history
 * review and stored on the referral. Answers so far survive "Back to
 * proforma": the proforma hands back the last `draft` this form reported
 * through `onDraftChange`.
 *
 * Self-contained (no Firebase): on confirm it calls `onConfirm` with the
 * structured decision; wiring to `rmsSubmitTriageDecision` happens at the
 * integration layer (RmsTriageProforma).
 *
 * onConfirm payload:
 *   { pathway: 'asd', accepted, evidence, otherFactors, conclusion,
 *     signposting, surfacedFactors, evidenceTags, cliniciansInvolved }
 */

const STEPS = [
  'Evidence of autism traits and impairment (DSM-5)',
  'Barriers to assessment & priority problems',
  'Triage decision',
];

const STEP_SHORT_LABELS = ['DSM-5 criteria', 'Barriers', 'Triage decision'];

const EVIDENCE_STEP = 0;
const BARRIERS_STEP = 1;
const DECISION_STEP = 2;
const PATHWAY_KEY = 'asd';

/**
 * The first review's answers as the second reviewer's starting answers
 * (TDI-401): value and threshold per criterion, nothing else, so the second
 * reviewer's own comments start empty.
 */
export function seedEvidenceFromFirstReview(firstReviewEvidence) {
  const seeded = {};
  Object.entries(firstReviewEvidence || {}).forEach(([key, item]) => {
    if (!item || typeof item !== 'object') return;
    const entry = {};
    if (item.value != null && item.value !== '') entry.value = item.value;
    if (item.threshold != null && item.threshold !== '') entry.threshold = item.threshold;
    if (Object.keys(entry).length) seeded[key] = entry;
  });
  return seeded;
}

/**
 * What the second reviewer did with each first-review answer (TDI-401):
 * 'agreed' when they kept it, 'changed' when they chose another. Evidence is
 * compared on value and threshold; the risks and priority problems questions
 * only when the second reviewer answered them, so an untouched optional
 * question is not reported as a disagreement.
 */
export function compareWithFirstReview(
  { evidence, riskPresent, priorityPresent },
  firstReviewEvidence,
  firstReviewOtherFactors,
) {
  const first = firstReviewEvidence || {};
  const keys = new Set([...Object.keys(first), ...Object.keys(evidence || {})]);
  const evidenceComparison = {};
  keys.forEach((key) => {
    const a = first[key] || {};
    const b = (evidence || {})[key] || {};
    const same = (a.value ?? null) === (b.value ?? null)
      && (a.threshold ?? null) === (b.threshold ?? null);
    evidenceComparison[key] = same ? 'agreed' : 'changed';
  });
  const otherFactors = {};
  const firstRisks = firstReviewOtherFactors?.risks || {};
  const firstPriority = firstReviewOtherFactors?.priorityProblems || {};
  if (riskPresent !== null && riskPresent !== undefined) {
    otherFactors.risks = riskPresent === (firstRisks.presentNeedingReduction ?? null) ? 'agreed' : 'changed';
  }
  if (priorityPresent !== null && priorityPresent !== undefined) {
    otherFactors.priorityProblems = priorityPresent === (firstPriority.present ?? null) ? 'agreed' : 'changed';
  }
  return { evidence: evidenceComparison, otherFactors };
}

/** The first review's answer to one criterion, in words. */
function firstReviewAnswerText(criterion, item) {
  const isAb = AUTISM_AB_CRITERION_KEY_SET.has(criterion.key);
  const valueLabel = isAb
    ? (AUTISM_IMPACT_SCALE.find((s) => s.id === item?.value)?.label || 'Not answered')
    : (AUTISM_YES_NO_OPTIONS.find((o) => o.id === item?.value)?.label || 'Not answered');
  const threshold = isAb && item?.threshold
    ? `; threshold met: ${item.threshold === 'yes' ? 'Yes' : 'No'}`
    : '';
  const comment = item?.comment ? `. Note: ${item.comment}` : '';
  return `${valueLabel}${threshold}${comment}`;
}

/** The "First review" line beside a question, in the existing amber style. */
function FirstReviewLine({ text }) {
  return (
    <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900 text-left">
      <span className="font-semibold">First review:</span> {text}
    </p>
  );
}

// Executes the active step's renderer as a child of the error boundary. Calling
// renderStep() inline in the wizard's own render would throw above the
// boundary (and unmount the whole wizard); routing it through a child
// component keeps the throw below the boundary.
function WizardStepContent({ render }) {
  return render();
}

// Criterion C onset follow-up applies by the yes/no answer (TD-241): yes =
// present early (known-early prompt), no = not present early (late-recognition
// prompt).
const C_EARLY_VALUES = ['yes'];
const C_LATE_VALUES = ['no'];

// The seven A/B criteria (the ones that carry an impact rating + threshold).
const AUTISM_AB_CRITERION_KEY_SET = new Set(AUTISM_DSM5_CRITERIA.map((c) => c.key));

// ── Conclusion-step summary of the clinician's own recorded answers (LSC-042
// Section 11). Regulatory boundary: the summary repeats what the clinician
// recorded — impact ratings, the threshold decision, sub-answers and evidence
// tags — and never computes, scores or suggests an outcome. Each chip's colour
// reflects only the value the clinician chose.
const RATING_LABELS = {
  ...Object.fromEntries(AUTISM_IMPACT_SCALE.map((s) => [s.id, s.label])),
  yes: 'Yes',
  no: 'No',
};

const RATING_CHIP_CLASSES = {
  significant_impact: 'bg-green-100 text-green-800 ring-green-300',
  partial_impact: 'bg-amber-100 text-amber-800 ring-amber-300',
  limited_impact: 'bg-gray-100 text-gray-600 ring-gray-300',
  yes: 'bg-green-100 text-green-800 ring-green-300',
  no: 'bg-gray-100 text-gray-600 ring-gray-300',
};

const REPORTED_BY_LABELS = Object.fromEntries(
  AUTISM_REPORTED_BY_OPTIONS.map((o) => [o.id, o.label]),
);
const LONG_TERM_LABELS = Object.fromEntries(
  AUTISM_LONG_TERM_OPTIONS.map((o) => [o.id, o.label]),
);

function RatingChip({ value }) {
  const label = RATING_LABELS[value];
  if (!label) {
    return (
      <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-gray-400 ring-1 ring-inset ring-gray-200">
        Not rated
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
        RATING_CHIP_CLASSES[value] || 'bg-gray-100 text-gray-600 ring-gray-300'
      }`}
    >
      {label}
    </span>
  );
}

/**
 * "What you recorded" — one row per criterion showing the clinician's own
 * rating, who-reports / long-standing answers and their evidence tags.
 */
function RecordedCriteriaSummary({ evidence, evidenceTags }) {
  const rows = [
    ...AUTISM_DSM5_CRITERIA.map((criterion) => {
      const item = evidence[criterion.key] || {};
      const tags = evidenceTags.filter((t) => t.criterionKey === criterion.key);
      const subAnswers = [
        (item.reportedBy || []).length
          ? `Reported by: ${item.reportedBy.map((id) => REPORTED_BY_LABELS[id] || id).join(', ')}`
          : null,
        item.longTerm ? `Long-standing: ${LONG_TERM_LABELS[item.longTerm] || item.longTerm}` : null,
        tags.length ? `${tags.length} evidence tag${tags.length === 1 ? '' : 's'}` : null,
      ].filter(Boolean);
      // A/B rows carry the impact rating plus the clinician's threshold yes/no.
      return { code: criterion.code, value: item.value, threshold: item.threshold, subAnswers };
    }),
    {
      code: 'C',
      value: evidence[AUTISM_CRITERION_C.key]?.value,
      subAnswers: [],
    },
    {
      code: 'D',
      value: evidence[AUTISM_CRITERION_D.key]?.value,
      subAnswers: [],
    },
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-left">
      <p className="text-sm font-semibold text-gray-900">What you recorded</p>
      <p className="mt-0.5 text-xs text-gray-500">
        A reference copy of your own ratings and evidence tags from step 1. It does not
        calculate, score or suggest an outcome — the conclusion below is entirely your
        decision.
      </p>
      <ul className="mt-3 divide-y divide-gray-100">
        {rows.map((row) => (
          <li key={row.code} className="flex items-start justify-between gap-3 py-1.5">
            <div className="flex min-w-0 items-start gap-2">
              <span className="w-7 shrink-0 text-xs font-semibold text-gray-700">{row.code}</span>
              {row.subAnswers.length > 0 && (
                <span className="text-[11px] text-gray-500">{row.subAnswers.join(' · ')}</span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <RatingChip value={row.value} />
              {/* A/B criteria also carry the threshold yes/no — labelled so it
                  never reads as the same axis as the impact rating. */}
              {'threshold' in row && (
                <span className="text-[11px] text-gray-500">
                  threshold <RatingChip value={row.threshold} />
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The unmet needs the clinician recorded earlier in the wizard, pulled through
 * mechanically (LSC-042 Sections 11–12) so the Conclusion and Signposting
 * steps never rely on memory.
 */
function RecordedUnmetNeeds({ needs, context }) {
  if (!needs.length) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-left">
      <p className="text-sm font-semibold text-gray-900">Unmet needs you recorded</p>
      <p className="mt-0.5 text-xs text-gray-500">{context}</p>
      <ul className="mt-2 space-y-1.5" aria-label="Recorded unmet needs">
        {needs.map((need) => (
          <li key={need.key} className="rounded-md border border-gray-200 bg-gray-50/70 px-2.5 py-1.5">
            <p className="text-xs font-medium text-gray-800">{need.label}</p>
            {need.detail && <p className="mt-0.5 text-[11px] text-gray-500">{need.detail}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AutismTriageDecisionForm({
  // patientName is still passed by the proforma; the page's shared identity
  // header shows it (TDI-392), so the wizard no longer reads it.
  patientSubject,
  // Loaded form schemas + instances, for the referral-evidence panel.
  schemas = {},
  formInstances = {},
  // The form answers related to each factor: { [factorKey]: [{ label, value,
  // respondent }] }. Not listed on any screen (R-016); recorded on the
  // decision as informant information for the letter.
  surfacedFactors = {},
  riskStratification,
  showRiskStratification = true,
  // Second review (TDI-401; Tom King, 23 Sept 2026: the first reviewer's
  // decisions and notes are shown so they are not wasted). The first review's
  // answers start pre-selected and are labelled "First review" beside each
  // question, with the first reviewer's tags and notes on the matching screen;
  // the second reviewer keeps each answer (agree) or changes it, and the
  // decision records which. Only the first reviewer's identity is withheld
  // (RMS-283).
  isSecondReview = false,
  firstReviewEvidence = null,
  firstReviewNotes = null,
  // The first review's otherFactors (risks and priority problems, and on a
  // review decided before TDI-394 its factor notes and group comments).
  firstReviewOtherFactors = null,
  // The first review's tags as snapshotted on its decision (TDI-394, R-008).
  firstReviewEvidenceTags = null,
  // The clinician's own evidence tags on this referral, and the handlers that
  // add and remove them. Owned by the parent so the same tags are shared with
  // the proforma and survive closing the wizard.
  evidenceTags = [],
  onAddTag,
  onRemoveTag,
  // The answers so far from the last time these screens were open, and where
  // to report them as they change.
  draft = null,
  onDraftChange,
  // The clinician's own answers from a decision they reopened to change
  // (TDI-695), in the draft's shape. The screens open with them; answers so
  // far (`draft`) win once the clinician has started again. On a second
  // review they take the place of the first-review pre-fill; the "First
  // review" lines still show.
  initialAnswers = null,
  currentClinician,
  // The referral is triaged on two pathways, so the check dialog names this
  // one (TDI-693).
  combinedReferral = false,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const seed = draft || initialAnswers;
  const [stepIndex, setStepIndex] = useState(() => seed?.stepIndex ?? 0);

  // Screen 1 — evidence answers, keyed by criterion:
  // { value, comment, reportedBy: [], longTerm, earlyKnownDetail, lateRecognitionDetail }.
  // On a second review each answer starts as the first review's (value and
  // threshold only; the first reviewer's comment is shown, not copied), so
  // keeping it is agreeing and choosing another is changing it (TDI-401).
  // A reopened decision starts from the clinician's own answers instead.
  const [evidence, setEvidence] = useState(() => {
    if (seed?.evidence) return seed.evidence;
    return isSecondReview && firstReviewEvidence ? seedEvidenceFromFirstReview(firstReviewEvidence) : {};
  });
  // Screen 2 — follow-through statuses on tagged support factors, the one
  // comment on other factors, and the risks and priority problems questions
  // (null until answered; drives the soft nudges).
  const [followThrough, setFollowThrough] = useState(() => seed?.followThrough ?? {});
  const [otherFactorsComment, setOtherFactorsComment] = useState(
    () => seed?.otherFactorsComment ?? '',
  );
  const [riskPresent, setRiskPresent] = useState(() => seed?.riskPresent ?? null);
  const [riskDetail, setRiskDetail] = useState(() => seed?.riskDetail ?? '');
  const [riskInterventions, setRiskInterventions] = useState(() => seed?.riskInterventions ?? '');
  const [priorityPresent, setPriorityPresent] = useState(() => seed?.priorityPresent ?? null);
  const [priorityPlans, setPriorityPlans] = useState(() => seed?.priorityPlans ?? null);
  const [priorityDetail, setPriorityDetail] = useState(() => seed?.priorityDetail ?? '');
  // Screen 3 — conclusion, decision and signposting.
  const [conclusion, setConclusion] = useState(() => seed?.conclusion ?? '');
  const [accepted, setAccepted] = useState(() => seed?.accepted ?? null);
  // Signposting pick-list (RMS-309): selected service ids, plus the
  // "Other, tell us more" title and text.
  const [signpostingSelected, setSignpostingSelected] = useState(
    () => seed?.signpostingSelected ?? [],
  );
  const [signpostingOtherOn, setSignpostingOtherOn] = useState(
    () => seed?.signpostingOtherOn ?? false,
  );
  const [signpostingOtherTitle, setSignpostingOtherTitle] = useState(
    () => seed?.signpostingOtherTitle ?? '',
  );
  const [signpostingOtherText, setSignpostingOtherText] = useState(
    () => seed?.signpostingOtherText ?? '',
  );
  const [panelOpen, setPanelOpen] = useState(false);
  // The "Check your decision" dialog (TDI-693).
  const [checkOpen, setCheckOpen] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Report the answers so far to the parent on every change, so going back to
  // the history review and returning picks up exactly here.
  useEffect(() => {
    onDraftChange?.({
      stepIndex,
      evidence,
      followThrough,
      otherFactorsComment,
      riskPresent,
      riskDetail,
      riskInterventions,
      priorityPresent,
      priorityPlans,
      priorityDetail,
      conclusion,
      accepted,
      signpostingSelected,
      signpostingOtherOn,
      signpostingOtherTitle,
      signpostingOtherText,
    });
    // onDraftChange is a fresh closure on every parent render; the answers
    // are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, evidence, followThrough, otherFactorsComment, riskPresent, riskDetail,
    riskInterventions, priorityPresent, priorityPlans, priorityDetail, conclusion, accepted,
    signpostingSelected, signpostingOtherOn, signpostingOtherTitle, signpostingOtherText]);

  const setEvidenceField = (key, field, value) =>
    setEvidence((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));

  const toggleReportedBy = (key, optionId) =>
    setEvidence((prev) => {
      const current = prev[key]?.reportedBy || [];
      const reportedBy = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      return { ...prev, [key]: { ...(prev[key] || {}), reportedBy } };
    });

  // The support factors the clinician tagged that take a follow-through
  // status, in group order. Follow-through is offered only beside what was
  // tagged (R2): an untagged factor is never prompted for.
  const followThroughFactors = otherFactorTagsByGroup(PATHWAY_KEY, evidenceTags).flatMap(
    ({ group, tags }) => group.factors.filter(
      (factor) => factor.supportsFollowThrough && tags.some((t) => t.factorKey === factor.factorKey),
    ),
  );

  // ── Step gating ────────────────────────────────────────────────────────────
  // Every criterion must be answered. A/B additionally force the threshold
  // yes/no (TD-241) so each criterion ends in a decision; C and D are a single
  // yes/no each.
  const evidenceComplete = AUTISM_EVIDENCE_KEYS.every((key) => {
    const item = evidence[key] || {};
    if (!item.value) return false;
    if (AUTISM_AB_CRITERION_KEY_SET.has(key)) return Boolean(item.threshold);
    return true;
  });
  const conclusionComplete = Boolean(conclusion.trim()) && accepted !== null;
  const risksUntouched = riskPresent === null;

  const canProceed = stepIndex === EVIDENCE_STEP ? evidenceComplete : true;
  const isLastStep = stepIndex === STEPS.length - 1;

  // The wizard renders inside a scrollable full-screen overlay it does not
  // own — always return to the top of the wizard on step change.
  const rootRef = useRef(null);
  useEffect(() => {
    // Optional-call: jsdom has no scrollIntoView.
    rootRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [stepIndex]);

  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));
  const goNext = () => {
    if (!canProceed) return;
    setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
  };

  const removeTagLabel = (tag) => `Remove ${tagChipLabel(PATHWAY_KEY, tag)} tag — ${tag.label}`;

  // "Complete referral decision" only opens the check; nothing is saved yet.
  const openCheck = () => {
    if (busy || !conclusionComplete || !evidenceComplete) return;
    setCheckOpen(true);
  };

  const signpostingLabels = AUTISM_SIGNPOSTING_OPTIONS
    .filter((o) => signpostingSelected.includes(o.optionId))
    .map((o) => o.label);

  // "Record decision": exactly what "Complete referral decision" did before
  // the check existed. The dialog closes once the save settles; a failed
  // save leaves the wizard open with every answer.
  const recordDecision = () => {
    const pending = handleConfirm();
    Promise.resolve(pending).finally(() => {
      if (mountedRef.current) setCheckOpen(false);
    });
  };

  const handleConfirm = () => {
    if (busy || !conclusionComplete || !evidenceComplete) return undefined;

    const evidencePayload = {};
    AUTISM_DSM5_CRITERIA.forEach((criterion) => {
      const item = evidence[criterion.key] || {};
      evidencePayload[criterion.key] = {
        // A/B carry the impact rating plus the forced threshold yes/no (TD-241).
        value: item.value || '',
        threshold: item.threshold || null,
        comment: (item.comment || '').trim(),
        reportedBy: item.reportedBy || [],
        longTerm: item.longTerm || null,
      };
    });
    const cItem = evidence[AUTISM_CRITERION_C.key] || {};
    // Only the follow-up matching the final rating persists — switching the
    // rating hides the other prompt, so its stale text must not reach the
    // audit record.
    evidencePayload[AUTISM_CRITERION_C.key] = {
      value: cItem.value || '',
      comment: '',
      earlyKnownDetail: C_EARLY_VALUES.includes(cItem.value)
        ? (cItem.earlyKnownDetail || '').trim()
        : '',
      lateRecognitionDetail: C_LATE_VALUES.includes(cItem.value)
        ? (cItem.lateRecognitionDetail || '').trim()
        : '',
    };
    const dItem = evidence[AUTISM_CRITERION_D.key] || {};
    evidencePayload[AUTISM_CRITERION_D.key] = {
      value: dItem.value || '',
      comment: (dItem.comment || '').trim(),
    };

    // Follow-through statuses, only where the clinician set one beside a
    // factor that is still tagged: removing the tag takes the select away,
    // so its status must not reach the record.
    const followThroughPayload = {};
    followThroughFactors.forEach((factor) => {
      const status = followThrough[factor.factorKey];
      if (status) followThroughPayload[factor.factorKey] = status;
    });

    // The form answers related to each factor, recorded on the decision as
    // informant information for the letter (R-016). Not shown on these
    // screens; the letter ranks them below the clinician's own record.
    const surfacedFactorsPayload = [];
    AUTISM_OTHER_FACTOR_GROUPS.forEach((group) => {
      group.factors.forEach((factor) => {
        const rows = surfacedFactors[factor.factorKey];
        // A malformed entry is left out rather than failing the decision.
        if (!Array.isArray(rows) || !rows.length) return;
        surfacedFactorsPayload.push({
          factorKey: factor.factorKey,
          factorLabel: factor.label,
          items: rows.map((r) => `${r.label}${r.respondent ? ` (${r.respondent})` : ''}: ${r.value}`),
          ...surfacedItemSources(rows),
        });
      });
    });

    return onConfirm({
      pathway: 'asd',
      accepted,
      evidence: evidencePayload,
      otherFactors: {
        // The one comment on other factors replaces the per-factor notes and
        // group comments of the earlier screens; support is now tagged.
        comment: otherFactorsComment.trim(),
        followThrough: followThroughPayload,
        risks: {
          // null when the optional questions were left untouched — recorded as
          // "unanswered" rather than an explicit "no".
          presentNeedingReduction: riskPresent,
          detail: riskDetail.trim(),
          interventions: riskInterventions.trim(),
        },
        priorityProblems: {
          present: priorityPresent,
          plansInPlace: priorityPresent === true ? priorityPlans : null,
          detail: priorityDetail.trim(),
        },
      },
      conclusion: conclusion.trim(),
      // The pick-list replaced the free-text signposting; the field stays for
      // shape compatibility with older decisions. The server re-sources every
      // selected label from its own list.
      signposting: '',
      signpostingSelections: signpostingSelected.map((optionId) => {
        const option = AUTISM_SIGNPOSTING_OPTIONS.find((o) => o.optionId === optionId);
        return { optionId, label: option ? option.label : optionId };
      }),
      signpostingOther: signpostingOtherOn && signpostingOtherTitle.trim()
        ? { title: signpostingOtherTitle.trim(), text: signpostingOtherText.trim() }
        : null,
      // The manual consensus tick was removed for go-live (TDI-784, R-053):
      // the platform has no consensus step, so no screen may promise one. The
      // server still auto-records a flag when the two outcomes disagree,
      // shown nowhere.
      consensusFlagRequested: false,
      // Per question, whether the second reviewer kept or changed the first
      // review's answer (TDI-401); null outside a second review.
      firstReviewComparison: isSecondReview && firstReviewEvidence
        ? compareWithFirstReview({ evidence, riskPresent, priorityPresent }, firstReviewEvidence, firstReviewOtherFactors)
        : null,
      surfacedFactors: surfacedFactorsPayload,
      evidenceTags: evidenceTags.map(decisionTagPayload),
      cliniciansInvolved: currentClinician?.clinicianId
        ? [{
            clinicianId: currentClinician.clinicianId,
            clinicianName: clinicianName(currentClinician),
            involved: true,
          }]
        : [],
    });
  };

  // ── Step renderers ─────────────────────────────────────────────────────────

  // The first review's answer and notes for this question (TDI-401).
  const renderFirstReviewAnswer = (criterion) => (
    isSecondReview && firstReviewEvidence
      ? <FirstReviewLine text={firstReviewAnswerText(criterion, firstReviewEvidence[criterion.key])} />
      : null
  );

  const renderCriterionSubQuestions = (key) => {
    const item = evidence[key] || {};
    return (
      <div className="mt-3 space-y-3">
        <div>
          <p className="text-xs font-medium text-gray-600">{AUTISM_REPORTED_BY_QUESTION}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={`Reported by — ${key}`}>
            {AUTISM_REPORTED_BY_OPTIONS.map((option) => {
              const selected = (item.reportedBy || []).includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleReportedBy(key, option.id)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${
                    selected
                      ? 'bg-primary text-white ring-primary'
                      : 'bg-white text-gray-600 ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-600">{AUTISM_LONG_TERM_QUESTION}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={`Long-standing — ${key}`}>
            {AUTISM_LONG_TERM_OPTIONS.map((option) => {
              const selected = item.longTerm === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setEvidenceField(key, 'longTerm', selected ? null : option.id)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${
                    selected
                      ? 'bg-primary text-white ring-primary'
                      : 'bg-white text-gray-600 ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        {/* Forced per-criterion threshold decision (TD-241) — the clinician's
            recorded judgement; nothing is computed from it. */}
        <fieldset>
          <legend className="text-xs font-medium text-gray-600">{AUTISM_THRESHOLD_QUESTION}</legend>
          <div className="mt-1.5 flex gap-2" role="group" aria-label={`Threshold — ${key}`}>
            {AUTISM_YES_NO_OPTIONS.map((option) => (
              <ToggleChoice
                key={option.id}
                name={`threshold-${key}`}
                label={option.label}
                checked={item.threshold === option.id}
                onChange={() => setEvidenceField(key, 'threshold', option.id)}
              />
            ))}
          </div>
        </fieldset>
      </div>
    );
  };

  const criterionTags = tagsOfKind(evidenceTags, TAG_KIND.CRITERION);

  const renderCriterionTags = (criterion) => (
    <TaggedLines
      heading={`Evidence you tagged as ${criterion.code}`}
      tags={criterionTags.filter((t) => t.criterionKey === criterion.key)}
      onRemoveTag={onRemoveTag}
      removeLabel={removeTagLabel}
    />
  );

  // The first review's ratings and written notes, summarised at the top of
  // the evidence step; each question also carries its own "First review" line
  // and starts from the first review's answer (TDI-401). Identity stays
  // withheld (RMS-283, 6 Aug: masking the name and professional title avoids
  // hierarchy bias), but the decisions and notes are shown so the first
  // reviewer's work is not wasted (Tom King, 23 Sept 2026).
  const renderFirstReviewDecisions = () => (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-left">
      <p className="text-xs font-semibold text-amber-900">
        First review decisions (reviewer identity withheld)
      </p>
      <p className="mt-0.5 text-xs text-amber-800">
        Shown for reference. You can agree with these or record different answers;
        your decision is the final one.
      </p>
      {firstReviewNotes ? (
        <div className="mt-2 rounded border border-amber-200 bg-white p-2">
          <p className="text-xs font-semibold text-amber-900">
            What the first reviewer wrote
          </p>
          <p className="mt-1 whitespace-pre-wrap text-xs text-gray-800">
            {firstReviewNotes}
          </p>
        </div>
      ) : null}
      <details className="mt-1.5">
        <summary className="cursor-pointer text-xs font-medium text-amber-800 underline">
          Show recorded ratings
        </summary>
        <ul className="mt-2 space-y-1 text-xs text-amber-900">
          {[...AUTISM_DSM5_CRITERIA, AUTISM_CRITERION_C, AUTISM_CRITERION_D].map((criterion) => {
            const item = firstReviewEvidence?.[criterion.key] || {};
            const isAb = AUTISM_AB_CRITERION_KEY_SET.has(criterion.key);
            const code = criterion.code
              || (criterion.key === AUTISM_CRITERION_C.key ? 'C' : 'D');
            const valueLabel = isAb
              ? (AUTISM_IMPACT_SCALE.find((s) => s.id === item.value)?.label || 'Not answered')
              : (AUTISM_YES_NO_OPTIONS.find((o) => o.id === item.value)?.label || 'Not answered');
            const threshold = isAb && item.threshold
              ? ` — threshold met: ${item.threshold === 'yes' ? 'Yes' : 'No'}`
              : '';
            return (
              <li key={criterion.key}>
                <span className="font-medium">{code}:</span> {valueLabel}{threshold}
              </li>
            );
          })}
        </ul>
      </details>
    </div>
  );

  const renderEvidenceStep = () => {
    const cValue = evidence[AUTISM_CRITERION_C.key]?.value;
    return (
      <div className="space-y-6">
        {firstReviewEvidence && renderFirstReviewDecisions()}
        <p className="text-xs text-gray-500">{AUTISM_EVIDENCE_INTRO}</p>
        {/* LSC-018 interim (RMS-199): EHCP / ed-psych reports cannot be
            uploaded, so when the referral indicates one exists the clinician
            is prompted to review it outside the system. */}
        {(surfacedFactors.ehcpStatus || []).length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-left">
            <p className="text-sm font-semibold text-gray-900">
              {AUTISM_EXTERNAL_EVIDENCE_NOTE_TITLE}
            </p>
            <p className="mt-1 text-xs text-gray-600">{AUTISM_EXTERNAL_EVIDENCE_NOTE}</p>
          </div>
        )}
        {AUTISM_CRITERIA_SECTIONS.map((section) => (
          <section key={section.id} aria-label={section.heading} className="space-y-4 text-left">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">{section.heading}</h4>
              <p className="mt-1 text-xs text-gray-500">{section.stem}</p>
              <p className="mt-0.5 text-xs text-gray-500">{section.note}</p>
            </div>
            {AUTISM_DSM5_CRITERIA.filter((c) => c.section === section.id).map((criterion) => (
              <EvidenceQuestion
                key={criterion.key}
                question={{ ...criterion, supportsComment: true }}
                answer={evidence[criterion.key]}
                scale={AUTISM_IMPACT_SCALE}
                onSelect={(value) => setEvidenceField(criterion.key, 'value', value)}
                onComment={(text) => setEvidenceField(criterion.key, 'comment', text)}
              >
                {renderFirstReviewAnswer(criterion)}
                {renderCriterionSubQuestions(criterion.key)}
                {renderCriterionTags(criterion)}
              </EvidenceQuestion>
            ))}
          </section>
        ))}

        {/* C and D are judgements about the whole picture, so every line the
            clinician tagged as evidence is shown once, to read as a whole. */}
        <TaggedLines heading="Evidence you tagged" tags={criterionTags} />

        <section aria-label={AUTISM_CRITERION_C.heading} className="space-y-3 text-left">
          <h4 className="text-sm font-semibold text-gray-900">{AUTISM_CRITERION_C.heading}</h4>
          <EvidenceQuestion
            question={{ key: AUTISM_CRITERION_C.key, label: AUTISM_CRITERION_C.label }}
            answer={evidence[AUTISM_CRITERION_C.key]}
            scale={AUTISM_YES_NO_OPTIONS}
            onSelect={(value) => setEvidenceField(AUTISM_CRITERION_C.key, 'value', value)}
            onComment={() => {}}
          >
            {renderFirstReviewAnswer(AUTISM_CRITERION_C)}
            {C_EARLY_VALUES.includes(cValue) && (
              <label className="mt-3 block text-xs font-medium text-gray-600">
                {AUTISM_CRITERION_C.earlyKnownPrompt}
                <textarea
                  value={evidence[AUTISM_CRITERION_C.key]?.earlyKnownDetail || ''}
                  onChange={(e) =>
                    setEvidenceField(AUTISM_CRITERION_C.key, 'earlyKnownDetail', e.target.value)
                  }
                  rows={2}
                  className="mt-1 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm font-normal focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </label>
            )}
            {C_LATE_VALUES.includes(cValue) && (
              <label className="mt-3 block text-xs font-medium text-gray-600">
                {AUTISM_CRITERION_C.lateRecognitionPrompt}
                <textarea
                  value={evidence[AUTISM_CRITERION_C.key]?.lateRecognitionDetail || ''}
                  onChange={(e) =>
                    setEvidenceField(AUTISM_CRITERION_C.key, 'lateRecognitionDetail', e.target.value)
                  }
                  rows={2}
                  className="mt-1 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm font-normal focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </label>
            )}
          </EvidenceQuestion>
        </section>

        <section aria-label={AUTISM_CRITERION_D.heading} className="space-y-3 text-left">
          <h4 className="text-sm font-semibold text-gray-900">{AUTISM_CRITERION_D.heading}</h4>
          <EvidenceQuestion
            question={{
              key: AUTISM_CRITERION_D.key,
              label: AUTISM_CRITERION_D.label,
              examples: AUTISM_CRITERION_D.helper,
              supportsComment: true,
            }}
            answer={evidence[AUTISM_CRITERION_D.key]}
            scale={AUTISM_YES_NO_OPTIONS}
            onSelect={(value) => setEvidenceField(AUTISM_CRITERION_D.key, 'value', value)}
            onComment={(text) => setEvidenceField(AUTISM_CRITERION_D.key, 'comment', text)}
          >
            {renderFirstReviewAnswer(AUTISM_CRITERION_D)}
          </EvidenceQuestion>
        </section>
      </div>
    );
  };

  /**
   * The first reviewer's risk and other-factor work on a second review
   * (R-008): their tags with notes, read from their decision; or, for a first
   * review decided before tags had kinds, their factor notes and group
   * comments, as shown before.
   */
  const renderFirstReviewFactors = () => {
    if (!isSecondReview) return null;
    const firstTags = firstReviewEvidenceTags || [];
    const hasKinds = firstTags.some((t) => t && t.kind);
    if (hasKinds) {
      const firstRisks = tagsOfKind(firstTags, TAG_KIND.RISK);
      const firstFactors = tagsOfKind(firstTags, TAG_KIND.OTHER_FACTOR);
      if (!firstRisks.length && !firstFactors.length) return null;
      return (
        <div className="space-y-1">
          <TaggedLines tags={firstRisks} firstReview />
          {otherFactorTagsByGroup(PATHWAY_KEY, firstFactors).map(({ group, tags }) => (
            <TaggedLines key={group.id} heading={group.label} tags={tags} firstReview />
          ))}
        </div>
      );
    }
    const notes = Object.entries(firstReviewOtherFactors?.notes || {})
      .filter(([, note]) => note)
      .map(([factorKey, note]) => {
        const group = AUTISM_OTHER_FACTOR_GROUPS.find((g) => g.factors.some((f) => f.factorKey === factorKey));
        return `${group ? factorLabelFor(PATHWAY_KEY, group.id, factorKey) : factorKey}: ${note}`;
      });
    const comments = AUTISM_OTHER_FACTOR_GROUPS
      .filter((g) => (firstReviewOtherFactors?.groupComments || {})[g.id])
      .map((g) => `${g.label}: ${firstReviewOtherFactors.groupComments[g.id]}`);
    const lines = [...notes, ...comments];
    if (!lines.length) return null;
    return (
      <div>
        {lines.map((line) => <FirstReviewLine key={line} text={line} />)}
      </div>
    );
  };

  const renderBarriersStep = () => {
    const factorGroups = otherFactorTagsByGroup(PATHWAY_KEY, evidenceTags);
    return (
      <div className="space-y-5 text-left">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-left">
          <p className="text-sm font-semibold text-gray-900">{AUTISM_RISKS_SECTION_TITLE}</p>
          <p className="mt-1 text-xs text-gray-500">{AUTISM_RISKS_SECTION_HINT}</p>
          {isSecondReview && firstReviewOtherFactors && (
            <FirstReviewLine
              text={[
                `Risks needing reduction: ${
                  firstReviewOtherFactors.risks?.presentNeedingReduction === true ? 'Yes'
                    : firstReviewOtherFactors.risks?.presentNeedingReduction === false ? 'No' : 'Not answered'
                }${firstReviewOtherFactors.risks?.detail ? ` (${firstReviewOtherFactors.risks.detail})` : ''}`,
                `Priority problems: ${
                  firstReviewOtherFactors.priorityProblems?.present === true ? 'Yes'
                    : firstReviewOtherFactors.priorityProblems?.present === false ? 'No' : 'Not answered'
                }${firstReviewOtherFactors.priorityProblems?.detail ? ` (${firstReviewOtherFactors.priorityProblems.detail})` : ''}`,
              ].join('. ')}
            />
          )}
          {showRiskStratification && (
            <RiskStratificationSummary riskStratification={riskStratification} />
          )}
          <TaggedLines
            heading="Risks you tagged"
            tags={tagsOfKind(evidenceTags, TAG_KIND.RISK)}
            onRemoveTag={onRemoveTag}
            removeLabel={removeTagLabel}
          />
          <fieldset className="mt-4">
            <legend className="text-sm text-gray-800">{AUTISM_RISKS_QUESTION_LABEL}</legend>
            <div className="mt-2 flex gap-2">
              <ToggleChoice
                name="risksPresent"
                label="Yes"
                checked={riskPresent === true}
                onChange={() => setRiskPresent(true)}
              />
              <ToggleChoice
                name="risksPresent"
                label="No"
                checked={riskPresent === false}
                onChange={() => setRiskPresent(false)}
              />
            </div>
          </fieldset>
          <textarea
            value={riskDetail}
            onChange={(e) => setRiskDetail(e.target.value)}
            rows={3}
            aria-label="Risk detail"
            placeholder="Detail (optional)"
            className="mt-3 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <label className="mt-4 block text-sm text-gray-800">
            {AUTISM_RISKS_INTERVENTIONS_LABEL}
            <span className="mt-0.5 block text-xs text-gray-500">
              {AUTISM_RISKS_INTERVENTIONS_HINT}
            </span>
            <textarea
              value={riskInterventions}
              onChange={(e) => setRiskInterventions(e.target.value)}
              rows={3}
              className="mt-1.5 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </label>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-left">
          {renderFirstReviewFactors()}
          {factorGroups.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-900">Other factors you tagged</p>
              {factorGroups.map(({ group, tags }) => (
                <TaggedLines
                  key={group.id}
                  heading={group.label}
                  tags={tags}
                  onRemoveTag={onRemoveTag}
                  removeLabel={removeTagLabel}
                />
              ))}
              {followThroughFactors.map((factor) => (
                <label key={factor.factorKey} className="mt-3 block text-xs font-medium text-gray-600">
                  {AUTISM_FOLLOW_THROUGH_QUESTION}
                  <span className="block font-normal text-gray-500">{factor.label}</span>
                  <select
                    value={followThrough[factor.factorKey] || ''}
                    onChange={(e) =>
                      setFollowThrough((prev) => ({
                        ...prev,
                        [factor.factorKey]: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-sm font-normal text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary sm:max-w-xs"
                  >
                    <option value="">Not recorded</option>
                    {AUTISM_FOLLOW_THROUGH_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          )}
          <label className="block text-xs font-medium text-gray-600">
            Comment on other factors (optional)
            <textarea
              value={otherFactorsComment}
              onChange={(e) => setOtherFactorsComment(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </label>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-left">
          <p className="text-sm font-semibold text-gray-900">Priority problems</p>
          <p className="mt-1 text-xs text-gray-500">{AUTISM_PRIORITY_SECTION_HINT}</p>
          <fieldset className="mt-4">
            <legend className="text-sm text-gray-800">
              {AUTISM_PRIORITY_PROBLEMS_QUESTION_LABEL}
            </legend>
            <div className="mt-2 flex gap-2">
              <ToggleChoice
                name="priorityProblemsPresent"
                label="Yes"
                checked={priorityPresent === true}
                onChange={() => setPriorityPresent(true)}
              />
              <ToggleChoice
                name="priorityProblemsPresent"
                label="No"
                checked={priorityPresent === false}
                onChange={() => setPriorityPresent(false)}
              />
            </div>
          </fieldset>
          {priorityPresent === true && (
            <fieldset className="mt-4">
              <legend className="text-sm text-gray-800">{AUTISM_PRIORITY_PLANS_QUESTION_LABEL}</legend>
              <div className="mt-2 flex gap-2">
                {AUTISM_PRIORITY_PLANS_OPTIONS.map((option) => (
                  <ToggleChoice
                    key={option.id}
                    name="priorityProblemsPlans"
                    label={option.label}
                    checked={priorityPlans === option.id}
                    onChange={() => setPriorityPlans(option.id)}
                  />
                ))}
              </div>
            </fieldset>
          )}
          <textarea
            value={priorityDetail}
            onChange={(e) => setPriorityDetail(e.target.value)}
            rows={3}
            aria-label="Priority problems detail"
            placeholder={AUTISM_PRIORITY_DETAIL_LABEL}
            className="mt-3 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
          <p className="text-xs text-blue-800">{AUTISM_RISKS_GATING_NOTE}</p>
        </div>
      </div>
    );
  };

  // Mechanical pull-through of recorded unmet needs for the Triage decision
  // screen (LSC-042 Sections 11–12).
  const recordedUnmetNeeds = collectRecordedUnmetNeeds({
    followThrough,
    riskPresent,
    riskDetail,
    priorityPresent,
    priorityPlans,
    priorityDetail,
  });

  const renderSignposting = () => (
    <div className="space-y-4 text-left">
      <div>
        <p className="text-base font-medium text-gray-900">{AUTISM_SIGNPOSTING_PROMPT}</p>
        <p className="mt-0.5 text-xs text-gray-500">{AUTISM_SIGNPOSTING_HINT}</p>
        <div className="mt-3 space-y-4" role="group" aria-label="Signposting services">
          {/* Grouped under the information pack's own headings (TDI-454), so
              a clinician finds an entry by heading rather than reading one
              long list. One tick per entry; a long list such as the books
              is a single tick. */}
          {AUTISM_SIGNPOSTING_GROUPS.map((group) => {
            const options = AUTISM_SIGNPOSTING_OPTIONS.filter((o) => o.group === group.groupId);
            if (options.length === 0) return null;
            return (
              <div key={group.groupId} role="group" aria-label={group.heading} className="space-y-2">
                <p className="text-sm font-semibold text-gray-800">{group.heading}</p>
                {options.map((option) => (
                  <label
                    key={option.optionId}
                    className="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={signpostingSelected.includes(option.optionId)}
                      onChange={(e) => setSignpostingSelected((prev) => (
                        e.target.checked
                          ? [...prev, option.optionId]
                          : prev.filter((id) => id !== option.optionId)
                      ))}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            );
          })}
          <label className="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={signpostingOtherOn}
              onChange={(e) => setSignpostingOtherOn(e.target.checked)}
            />
            <span>{SIGNPOSTING_OTHER_LABEL}</span>
          </label>
          {signpostingOtherOn && (
            <div className="ml-6 space-y-2">
              <input
                type="text"
                value={signpostingOtherTitle}
                onChange={(e) => setSignpostingOtherTitle(e.target.value)}
                aria-label="Other service name"
                placeholder="Service or support name"
                className="block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <textarea
                value={signpostingOtherText}
                onChange={(e) => setSignpostingOtherText(e.target.value)}
                rows={3}
                aria-label="Other service details"
                placeholder="What the family should know about it"
                className="block w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          )}
        </div>
      </div>
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
        <p className="text-xs text-blue-800">
          Each selected service appears in the letter under its own heading. Until the
          confirmed descriptions arrive from the service, a holding sentence renders
          beneath each heading.
        </p>
      </div>
    </div>
  );

  const renderDecisionStep = () => (
    <div className="space-y-5 text-left">
      <RecordedCriteriaSummary evidence={evidence} evidenceTags={criterionTags} />
      <RecordedUnmetNeeds
        needs={recordedUnmetNeeds}
        context="Support gaps, risks and priority problems exactly as you recorded them in the earlier steps."
      />
      <div>
        <p className="text-base font-medium text-gray-900">{CONCLUSION_PROMPT}</p>
        <ul className="mt-2 ml-4 list-disc text-xs text-gray-500">
          {AUTISM_CONCLUSION_HELPER_PROMPTS.map((prompt) => (
            <li key={prompt}>{prompt}</li>
          ))}
        </ul>
        {isSecondReview && firstReviewNotes && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-left">
            <p className="text-xs font-semibold text-amber-900">First review conclusion (reviewer identity withheld)</p>
            <p className="mt-1 whitespace-pre-wrap text-xs text-gray-800">{firstReviewNotes}</p>
          </div>
        )}
        {/* What the clinician added to their conclusion while reading sits
            beside the box they write it in. */}
        <TaggedLines
          heading="Added to your conclusion"
          tags={tagsOfKind(evidenceTags, TAG_KIND.CONCLUSION)}
          onRemoveTag={onRemoveTag}
          removeLabel={removeTagLabel}
        />
        <textarea
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          rows={6}
          aria-label="Your conclusion"
          className="mt-3 block w-full rounded-lg border-2 border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        />
      </div>

      <fieldset>
        <legend className="text-base font-medium text-gray-900">
          {AUTISM_ACCEPT_QUESTION_LABEL}
        </legend>
        <div className="mt-2 flex gap-2">
          <ToggleChoice
            name="acceptDecision"
            label="Yes"
            checked={accepted === true}
            onChange={() => setAccepted(true)}
          />
          <ToggleChoice
            name="acceptDecision"
            label="No"
            checked={accepted === false}
            onChange={() => setAccepted(false)}
          />
        </div>
      </fieldset>

      {renderSignposting()}
    </div>
  );

  const renderStep = () => {
    if (stepIndex === EVIDENCE_STEP) return renderEvidenceStep();
    if (stepIndex === BARRIERS_STEP) return renderBarriersStep();
    if (stepIndex === DECISION_STEP) return renderDecisionStep();
    return null;
  };

  return (
    <div ref={rootRef} className="mx-auto flex max-w-3xl flex-col gap-6 text-left">
      <div>
        <StepProgress labels={STEP_SHORT_LABELS} current={stepIndex} />
        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              Step {stepIndex + 1} of {STEPS.length}
            </p>
            <h3 className="mt-0.5 text-xl font-semibold text-gray-900">{STEPS[stepIndex]}</h3>
            {/* The child's identity sits in the page's shared header on every
                step (TDI-392), so it is not repeated here. */}
          </div>
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="whitespace-nowrap rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            View referral evidence
          </button>
        </div>
      </div>

      {/* Patient context strip, kept visible while weighing barriers and factors. */}
      {stepIndex === BARRIERS_STEP && <PatientProfileStrip subject={patientSubject} />}

      {/* The boundary sits below the wizard's state, so a crash while rendering
          one step cannot unmount the wizard or discard the answers on other
          steps; keying by step lets Back/Next leave a failed step normally. */}
      <div className="min-h-[220px]">
        <RmsProviderErrorBoundary inline key={stepIndex}>
          <WizardStepContent render={renderStep} />
        </RmsProviderErrorBoundary>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={stepIndex === 0 ? onCancel : goBack}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          {stepIndex === 0 ? 'Cancel' : 'Back'}
        </button>
        <div className="flex items-center gap-3">
          {stepIndex === BARRIERS_STEP && risksUntouched && (
            <p className="text-xs text-amber-700">
              The risks question has not been answered — you can still continue.
            </p>
          )}
          {isLastStep ? (
            <button
              type="button"
              onClick={openCheck}
              disabled={busy || !conclusionComplete}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                !busy && conclusionComplete
                  ? 'bg-primary hover:bg-primary/90'
                  : 'cursor-not-allowed bg-gray-300'
              }`}
            >
              Complete referral decision
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              disabled={!canProceed}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                canProceed ? 'bg-primary hover:bg-primary/90' : 'cursor-not-allowed bg-gray-300'
              }`}
            >
              Next
            </button>
          )}
        </div>
      </div>

      <TriageConfirmDialog
        open={checkOpen}
        onOpenChange={setCheckOpen}
        title={DECISION_CHECK_TITLE}
        subtitle={decisionCheckSubtitle(PATHWAY_KEY, combinedReferral)}
        lines={decisionCheckLines({
          pathway: PATHWAY_KEY,
          accepted: accepted === true,
          isSecondReview,
          signpostingLabels,
          signpostingOtherTitle: signpostingOtherOn ? signpostingOtherTitle : '',
          // The decision can be changed until the letter is signed off, or a
          // first rejection until a second reviewer picks it up (TDI-695).
          showChangeLine: true,
        })}
        cancelLabel={DECISION_CHECK_GO_BACK}
        confirmLabel={DECISION_CHECK_RECORD}
        busy={busy}
        onConfirm={recordDecision}
      />

      <ReferralEvidencePanel
        pathway="asd"
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        schemas={schemas}
        formInstances={formInstances}
        tags={evidenceTags}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
      />
    </div>
  );
}
